import Icon from '@/components/atoms/Icon';
import Button from '@/components/atoms/Button/Button';
import IconButton from '@/components/atoms/Button/IconButton';

export interface PanelTab {
  id: string;
  label: string;
  icon?: string;
  trackingId?: string;
}

type ClipCustomizerPanelProps = {
  heading: string;
  onClose: () => void;
  closeTrackingId?: string;
  subHeading?: string;
  children: React.ReactNode;
} & (
  | { tabs?: undefined; activeTab?: never; onTabChange?: never }
  | { tabs: PanelTab[]; activeTab: string; onTabChange: (id: string) => void }
);

export default function ClipCustomizerPanel({
  heading,
  onClose,
  closeTrackingId = 'panel-close',
  subHeading,
  children,
  tabs,
  activeTab,
  onTabChange
}: ClipCustomizerPanelProps) {
  const hasSubHeadingOrTabs = subHeading !== undefined || (tabs !== undefined && tabs.length > 0);

  return (
    <div className="flex h-[calc(100vh-84px)] w-[402px] flex-col overflow-hidden rounded-[16px] border bg-white shadow">
      <div className="sticky top-0 z-10 bg-white px-4 pt-4">
        <div className={`flex items-center justify-between ${hasSubHeadingOrTabs ? 'mb-1' : 'mb-6'}`}>
          <h2 className="text-[18px] font-medium leading-[28px] -tracking-normal text-black">{heading}</h2>
          <IconButton
            icon="IconX"
            trackingId={closeTrackingId}
            onClick={onClose}
            variation="none"
            title={`Close ${heading.toLowerCase()}`}
            buttonClassName="rounded p-0.5 text-black hover:bg-slate-100 hover:text-gray-800"
          />
        </div>
        {subHeading && <p className="mb-4 text-sm font-medium text-slate-600">{subHeading}</p>}
        {tabs && tabs.length > 0 && (
          <div className="flex border-b border-slate-200">
            {tabs.map(tab => (
              <Button
                key={tab.id}
                trackingId={tab.trackingId ?? `panel-tab-${tab.id}`}
                onClick={() => onTabChange?.(tab.id)}
                variation="none"
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-none pb-2 !text-sm font-medium transition-colors ${
                  activeTab === tab.id ? 'border-b-2 border-gray-900 text-black' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon && <Icon name={tab.icon as any} className="size-4" />}
                {tab.label}
              </Button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4">{children}</div>
    </div>
  );
}
